import { isPlatformBrowser } from '@angular/common';
import { DOCUMENT, Injectable, PLATFORM_ID, inject } from '@angular/core';
import {
  ActivatedRoute,
  ActivatedRouteSnapshot,
  Params,
  Router,
  UrlTree
} from '@angular/router';

import { ToastController } from '@gruposaltaedu/liv-design-system/components';
import { I18nService } from '@gruposaltaedu/liv-i18n/core';
import { EMPTY, Observable, of, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { Features } from 'src/app/core/constants/features';
import { isBrazilExclusiveFeatureVisible } from 'src/app/core/helpers/locale-visibility.helper';
import { encryptObfuscated } from 'src/app/core/security/state-cypher';
import { ApiLiv } from 'src/app/core/services/api/api-liv.service';
import { AuthStateService } from 'src/app/core/services/auth/auth-state.service';
import { PermissionService } from 'src/app/core/services/auth/permission.service';
import { UserProfileService } from 'src/app/core/services/auth/user-profile.service';
import { SidebarMenuStateService } from 'src/app/shell/services/sidebar-menu-state.service';
import { UserSettingsMenuStateService } from 'src/app/shell/services/user-settings-menu-state.service';
import { environment } from 'src/environments/environment';

export const MY_WORLD_REDIRECT_QUERY_PARAM = 'myWorld';
export const MY_WORLD_REDIRECT_QUERY_VALUE = '1';

export type MyWorldRedirectErrorCode =
  'NO_ACCESS' | 'INVALID_SESSION' | 'REDIRECT_FAILED';

export class MyWorldRedirectError extends Error {
  constructor(public readonly code: MyWorldRedirectErrorCode) {
    super(code);
  }
}

@Injectable({ providedIn: 'root' })
export class MyWorldRedirectService {
  private readonly router = inject(Router);
  private readonly apiLiv = inject(ApiLiv);
  private readonly authStateService = inject(AuthStateService);
  private readonly userProfileService = inject(UserProfileService);
  private readonly permissionService = inject(PermissionService);
  private readonly i18n = inject(I18nService);
  private readonly toast = inject(ToastController);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly document = inject(DOCUMENT);
  private readonly userSettingsMenuState = inject(UserSettingsMenuStateService);
  private readonly sidebarMenuState = inject(SidebarMenuStateService);

  /** Query params from {@link Router} navigation (e.g. `initialUrl.queryParams`). */
  hasRedirectIntentInQuery(queryParams: Params): boolean {
    return this.hasIntentInQueryParams(queryParams);
  }

  /** Serialized app URL (e.g. `RouterStateSnapshot.url`). */
  hasRedirectIntentInUrl(url: string): boolean {
    try {
      const tree = this.router.parseUrl(url);

      if (this.hasIntentInQueryParams(tree.queryParams)) {
        return true;
      }

      const redirect = this.getQueryParam(tree.queryParams, 'redirect');
      return redirect ? this.hasRedirectIntentInUrl(redirect) : false;
    } catch {
      return false;
    }
  }

  hasLoginIntentFromRoute(route: ActivatedRoute): boolean {
    const paramMap = route.snapshot.queryParamMap;
    const redirect = paramMap.get('redirect');

    return (
      this.isActiveParam(paramMap.get(MY_WORLD_REDIRECT_QUERY_PARAM)) ||
      (redirect ? this.hasRedirectIntentInUrl(redirect) : false)
    );
  }

  createBridgeUrlTree(lang: string): UrlTree {
    return this.router.createUrlTree(['/', lang, 'auth', 'my-world']);
  }

  resolveLangFromRoute(route: ActivatedRoute | ActivatedRouteSnapshot): string {
    const snapshot = 'snapshot' in route ? route.snapshot : route;
    let current: ActivatedRouteSnapshot | null = snapshot;

    while (current) {
      const lang = current.params['lang'];
      if (lang && this.i18n.isSupportedLanguage(lang)) {
        return lang;
      }
      current = current.parent;
    }

    return this.i18n.currentLanguage();
  }

  resolvePostLoginPath(lang: string, redirect: string | null): string {
    if (!redirect) {
      return `/${lang}/inicio`;
    }

    const path = redirect.startsWith('/')
      ? redirect
      : `/${lang}/${redirect.replace(/^\//, '')}`;

    return this.replaceLocalePrefix(path, lang);
  }

  isAuthLoginEntryUrl(url: string): boolean {
    const pathname = this.toAppUrl(url).pathname;
    return /\/auth\/login\/?$/.test(pathname) || /\/auth\/?$/.test(pathname);
  }

  resolveAuthenticatedAuthEntryRedirect(url: string): UrlTree | null {
    if (
      !this.authStateService.isAuthenticated ||
      !this.isAuthLoginEntryUrl(url)
    ) {
      return null;
    }

    const parsed = this.router.parseUrl(url);
    const lang = this.extractLangFromUrl(url) ?? this.i18n.currentLanguage();
    const redirect = this.getQueryParam(parsed.queryParams, 'redirect');
    const hasIntent =
      this.hasRedirectIntentInQuery(parsed.queryParams) ||
      (redirect ? this.hasRedirectIntentInUrl(redirect) : false);

    if (hasIntent) {
      return this.createBridgeUrlTree(lang);
    }

    return this.router.parseUrl(this.resolvePostLoginPath(lang, redirect));
  }

  executeBridgeRedirect(): void {
    this.redirectWithFallback();
  }

  navigateToBridge(lang?: string): void {
    const targetLang = lang ?? this.i18n.currentLanguage();
    void this.router.navigate(['/', targetLang, 'auth', 'my-world'], {
      replaceUrl: true
    });
  }

  resolveAttemptedUrl(segmentPath: string): string {
    const navigation = this.router.getCurrentNavigation();
    const fromNavigation =
      navigation?.finalUrl?.toString() ?? navigation?.extractedUrl?.toString();

    if (fromNavigation) {
      return this.pathAndSearchFromRouterUrl(fromNavigation);
    }

    const lang = this.i18n.currentLanguage();
    const pathname = segmentPath
      ? `/${lang}/${segmentPath.replace(/^\//, '')}`
      : `/${lang}`;

    const search = this.queryParamsToSearch(
      this.router.routerState.snapshot.root.queryParams
    );

    return search ? `${pathname}?${search}` : pathname;
  }

  tryHandleAuthenticatedAccess(attemptedUrl: string): boolean {
    if (!this.hasRedirectIntentInUrl(attemptedUrl)) {
      return false;
    }

    this.navigateToBridge(this.extractLangFromUrl(attemptedUrl) ?? undefined);
    return true;
  }

  navigateToLogin(attemptedUrl: string, lang: string): void {
    const targetLang = this.extractLangFromUrl(attemptedUrl) ?? lang;

    void this.router.navigate(['/', targetLang, 'auth', 'login'], {
      queryParams: this.buildLoginQueryParams(attemptedUrl)
    });
  }

  buildElevaReturnUrl(
    domain: string,
    lang: string,
    route: ActivatedRoute
  ): string {
    if (this.hasLoginIntentFromRoute(route)) {
      return `${domain}/${lang}/auth/my-world`;
    }

    return `${domain}/${lang}/auth/login`;
  }

  redirectAfterLogin(route: ActivatedRoute): Observable<void> {
    if (!this.hasLoginIntentFromRoute(route)) {
      return of(undefined);
    }

    this.navigateToBridge();
    return of(undefined);
  }

  navigateInternalAfterLogin(route: ActivatedRoute): void {
    const lang = this.resolveLangFromProfileOrRoute(route);
    const redirect = route.snapshot.queryParamMap.get('redirect');

    void this.router.navigateByUrl(this.resolvePostLoginPath(lang, redirect), {
      replaceUrl: true
    });
  }

  redirectToMyWorld(): Observable<void> {
    if (!this.hasMyWorldAccess()) {
      return throwError(() => new MyWorldRedirectError('NO_ACCESS'));
    }

    const { id } = this.userProfileService.profileSnapshot || {};
    const { token } = this.authStateService.sessionSnapshot || {};

    if (id === undefined || !token) {
      return throwError(() => new MyWorldRedirectError('INVALID_SESSION'));
    }

    return this.apiLiv.get<string>('/url/MyWorld').pipe(
      map((redirectUrl) => this.buildTargetUrl(redirectUrl, id)),
      tap((targetUrl) => this.navigateExternally(targetUrl)),
      map((): void => undefined),
      catchError((error) => {
        if (error instanceof MyWorldRedirectError) {
          return throwError(() => error);
        }
        return throwError(() => new MyWorldRedirectError('REDIRECT_FAILED'));
      })
    );
  }

  private buildLoginQueryParams(attemptedUrl: string): Record<string, string> {
    if (!this.hasRedirectIntentInUrl(attemptedUrl)) {
      return { redirect: attemptedUrl };
    }

    const parsed = this.toAppUrl(attemptedUrl);
    parsed.searchParams.delete(MY_WORLD_REDIRECT_QUERY_PARAM);

    return {
      [MY_WORLD_REDIRECT_QUERY_PARAM]: MY_WORLD_REDIRECT_QUERY_VALUE,
      redirect: parsed.pathname + parsed.search
    };
  }

  private redirectWithFallback(): void {
    this.redirectToMyWorld()
      .pipe(
        catchError((error) => {
          this.handleRedirectError(error);
          return EMPTY;
        })
      )
      .subscribe();
  }

  private handleRedirectError(error: unknown): void {
    this.showErrorToast(error);
    const lang = this.i18n.currentLanguage();
    void this.router.navigateByUrl(`/${lang}/inicio`, { replaceUrl: true });
  }

  private resolveLangFromProfileOrRoute(route: ActivatedRoute): string {
    const country = this.userProfileService.profileSnapshot?.country;
    const fromCountry = country
      ? this.i18n.getLanguageFromCountry(country)
      : null;

    return fromCountry ?? this.resolveLangFromRoute(route);
  }

  private replaceLocalePrefix(url: string, lang: string): string {
    try {
      const parsed = this.toAppUrl(url);
      const segments = parsed.pathname.split('/');

      if (segments.length > 1 && this.i18n.isSupportedLanguage(segments[1])) {
        segments[1] = lang;
      } else {
        segments.splice(1, 0, lang);
      }

      return `${segments.join('/')}${parsed.search}${parsed.hash}`;
    } catch {
      return url;
    }
  }

  private extractLangFromUrl(url: string): string | null {
    try {
      const segment = this.toAppUrl(url).pathname.split('/').filter(Boolean)[0];
      return segment && this.i18n.isSupportedLanguage(segment) ? segment : null;
    } catch {
      return null;
    }
  }

  private pathAndSearchFromRouterUrl(url: string): string {
    const parsed = this.toAppUrl(url);
    return parsed.pathname + parsed.search;
  }

  private toAppUrl(url: string): URL {
    const base = isPlatformBrowser(this.platformId)
      ? (this.document.location?.origin ?? 'http://localhost')
      : 'http://localhost';

    return new URL(url, base);
  }

  private navigateExternally(url: string): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.userSettingsMenuState.close();
    this.sidebarMenuState.collapse();
    this.document.defaultView?.location.assign(url);
  }

  private hasMyWorldAccess(): boolean {
    if (!isBrazilExclusiveFeatureVisible(this.i18n.currentLanguage())) {
      return false;
    }
    return this.permissionService.hasFeature(Features.myWorld)();
  }

  private hasIntentInQueryParams(queryParams: Params): boolean {
    return this.isActiveParam(
      this.getQueryParam(queryParams, MY_WORLD_REDIRECT_QUERY_PARAM)
    );
  }

  private getQueryParam(params: Params, key: string): string | null {
    const value = params[key];

    if (Array.isArray(value)) {
      const first = value[0];
      return first === undefined || first === null ? null : String(first);
    }

    return value === undefined || value === null ? null : String(value);
  }

  private queryParamsToSearch(queryParams: Params): string {
    const entries: [string, string][] = [];

    for (const [key, value] of Object.entries(queryParams)) {
      if (value === null || value === undefined) {
        continue;
      }

      if (Array.isArray(value)) {
        for (const item of value) {
          entries.push([key, String(item)]);
        }
        continue;
      }

      entries.push([key, String(value)]);
    }

    return new URLSearchParams(entries).toString();
  }

  private isActiveParam(value: string | null): boolean {
    return value === MY_WORLD_REDIRECT_QUERY_VALUE || value === 'true';
  }

  private buildTargetUrl(redirectUrl: string, userId: number): string {
    const apiUrl = redirectUrl.startsWith('http')
      ? new URL(redirectUrl)
      : new URL(redirectUrl, environment.apiUrl);

    const accessToken = apiUrl.searchParams.get('token') ?? '';

    if (!accessToken) {
      throw new MyWorldRedirectError('REDIRECT_FAILED');
    }

    const targetUrl = environment.myWorldAppUrl
      ? new URL(apiUrl.pathname, environment.myWorldAppUrl)
      : apiUrl;

    const encryptedToken = encryptObfuscated(
      { token: accessToken, userId },
      environment.myWorldCypherSecret
    );

    targetUrl.searchParams.set('token', encodeURIComponent(encryptedToken));

    return targetUrl.toString();
  }

  private showErrorToast(error: unknown): void {
    const message =
      error instanceof MyWorldRedirectError && error.code === 'NO_ACCESS'
        ? this.i18n.instant('my_world_no_access')
        : `${this.i18n.instant('my_world_redirect_error')} <strong>Meu Mundo</strong>.`;

    this.toast.warning({ message });
  }
}
